"use client"

import React, { useEffect, useState } from "react";
import NavbarProfile from "./navbarProfile";
import Image from 'next/image'
import NavbarLiveStatus from "./navbarLiveStatus";
import Item from "@mui/material/Grid"
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ChatIcon from '@mui/icons-material/Chat';
import TagIcon from '@mui/icons-material/Tag';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article'
import HighlightIcon from '@mui/icons-material/Highlight';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import NavbarItem from "./navbarItem";
import { socket } from "@/app/util/common/socket";
import { usePathname } from "next/navigation";
import BorderOuterIcon from '@mui/icons-material/BorderOuter';
import ShortcutIcon from '@mui/icons-material/Shortcut';
import { useTheme } from "@/app/util/theme/themeProvider";

const NavbarContainer = (props: {
    isAdmin: boolean,
    isDeveloper: boolean,
    hasQueuePermission: boolean,
    hasGraphicsPermission: boolean,
    hasGuestAdminPermission: boolean,
    isLive: boolean,
    testModus: boolean,
    user: {
        image: string | null,
        id: string | null,
        name: string | null
    }
}) => {
    const [hidden, setHidden] = useState(false)
    const [connectionStatus, setConnectionStatus] = useState("")
    const icon = (hidden) ? <ArrowForwardIosIcon /> : <ArrowBackIosIcon />
    const path = usePathname();
    const pathWithSocket = path.includes("/messages") && !path.includes("create")

    const { theme } = useTheme();
    const isLight = theme === "light";    
    const logoSrc = isLight ? "/eda_logo_light_theme.png" : "/eda_logo_dark_theme.png"

    useEffect(() => {
        const intervalId = setInterval(() => {
            setConnectionStatus((socket?.connected) ? "socket-connected" : "socket-disconnected")
        }, 1000)
        
        return () => {
            clearInterval(intervalId)
        }
    }, [socket])

    return (
        <div className={`navbar ${(hidden) ? "navbar-hidden" : ""}`}>
            <span className="logo-container">
                {
                    (pathWithSocket) ? <span className={`socket-connection-status ${connectionStatus}`}></span> : <></> 
                }
                <a href="/" className="root-url">
                    <Image
                        src={logoSrc}
                        alt="everyday astronaut logo"
                        width={300}
                        height={74}
                        priority
                    />
                </a>
                <button className="collapse-navbar" onClick={() => setHidden(!hidden)}>
                    { icon }
                </button>
            </span>
            <NavbarLiveStatus isLive={props.isLive} testModus={props.testModus} hidden={hidden}></NavbarLiveStatus>
            <hr />
            <div className="navbar-items-container">
                {
                    (props.hasQueuePermission) ? (
                        <Item>
                            <div className={`nav-block-title ${(hidden) ? "nav-items-hidden" : ""}`}>
                                <a href="#">
                                    <DashboardIcon></DashboardIcon>
                                    Manage Chat
                                    <ExpandMoreIcon></ExpandMoreIcon>
                                </a>
                            </div>
                            <nav className={(hidden) ? "nav-items-hidden" : ""}>
                                <NavbarItem
                                    title="messages"
                                    url="/messages"
                                    icon={<ChatIcon></ChatIcon>}
                                ></NavbarItem>
                                <NavbarItem
                                    title="discord messages"
                                    url="/messages/discord"
                                    icon={<PeopleAltIcon></PeopleAltIcon>}
                                ></NavbarItem>
                                <NavbarItem
                                    title="youtube messages"
                                    url="/messages/youtube"
                                    icon={<PeopleAltIcon></PeopleAltIcon>}
                                ></NavbarItem>
                                <NavbarItem
                                    title="mentioned messages"
                                    url="/messages/mentioned"
                                    icon={<TagIcon></TagIcon>}
                                ></NavbarItem>
                                <NavbarItem
                                    title="queued messages"
                                    url="/messages/queue"
                                    icon={<TagIcon></TagIcon>}
                                ></NavbarItem>
                                <NavbarItem
                                    title="highlighted messages"
                                    url="/messages/highlighted"
                                    icon={<HighlightIcon></HighlightIcon>}
                                ></NavbarItem>
                                <NavbarItem
                                    title="create message"
                                    url="/messages/create"
                                    icon={<AddIcon></AddIcon>}
                                ></NavbarItem>
                            </nav>
                        </Item> 
                    ): <></>
                }   
                {
                    (props.hasGraphicsPermission) ? (
                        <Item>
                            <div className={`nav-block-title ${(hidden) ? "nav-items-hidden" : ""}`}>
                                <a href="#">
                                    <DashboardIcon></DashboardIcon>
                                    Manage Graphics
                                    <ExpandMoreIcon></ExpandMoreIcon>
                                </a>
                            </div>
                            <nav className={(hidden) ? "nav-items-hidden" : ""}>
                                <NavbarItem
                                    title="graphics Editor"
                                    url="/graphics/edit"
                                    icon={<BorderOuterIcon></BorderOuterIcon>}
                                ></NavbarItem>
                                <NavbarItem
                                    title="graphics UI"
                                    url="/graphics/ui"
                                    icon={<WebAssetIcon></WebAssetIcon>}
                                ></NavbarItem>
                            </nav>
                        </Item>
                    ) : <></>
                }
                {
                    (props.isAdmin || props.hasGuestAdminPermission) ? (
                        <Item>
                            <div className={`nav-block-title ${(hidden) ? "nav-items-hidden" : ""}`}>
                                <a href="#">
                                    <DashboardIcon></DashboardIcon>
                                    Manage Chat Administrator
                                    <ExpandMoreIcon></ExpandMoreIcon>
                                </a>
                            </div>
                            {
                                (props.isAdmin) ? (
                                    <nav className={(hidden) ? "nav-items-hidden" : ""}>
                                        <NavbarItem
                                            title="settings"
                                            url="/admin"
                                            icon={<SettingsIcon></SettingsIcon>}
                                        ></NavbarItem>
                                        <NavbarItem
                                            title="abbreviations"
                                            url="/admin/abbreviations"
                                            icon={<ShortcutIcon></ShortcutIcon>}
                                        ></NavbarItem>
                                        <NavbarItem
                                            title="changelog"
                                            url="/admin/changelog"
                                            icon={<ArticleIcon></ArticleIcon>}
                                        ></NavbarItem>
                                        {
                                            (props.hasQueuePermission) ? (
                                                <NavbarItem
                                                    title="minimal queue"
                                                    url="/admin/queue"
                                                    icon={<ClearAllIcon></ClearAllIcon>}
                                                ></NavbarItem>
                                            ) : <></>
                                        }
                                    </nav>
                                ) : <></>
                            }
                            {
                                (props.hasGuestAdminPermission || props.isAdmin || props.isDeveloper) ? (
                                    <nav className={(hidden) ? "nav-items-hidden" : ""}>
                                        <NavbarItem
                                            title="#ASK Questions confirm"
                                            url="/messages/ask-confirm"
                                            icon={<PeopleAltIcon></PeopleAltIcon>}
                                        ></NavbarItem>
                                        <NavbarItem
                                            title="#ASK Questions push to stream"
                                            url="/messages/ask"
                                            icon={<PeopleAltIcon></PeopleAltIcon>}
                                        ></NavbarItem>
                                    </nav>
                                ) : <></>
                            }
                        </Item>
                    ) : <></>
                }
            </div>
            <NavbarProfile hidden={hidden} profileImage={props.user.image} profileName={props.user.name} profileIdentifier={props.user.id}></NavbarProfile>
        </div>
    );
};

export default NavbarContainer;